// pages/Sales/Sales.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import TitleSetter from "../../component/TitleSetter";
import CircularProgress from "@mui/material/CircularProgress";
import { getAuthCredentials } from "../../utils/auth";

import SalesInventory from "./SalesInventory";
import SalesTransactions from "./SalesTransactions";
import SalesExport from "./SalesExport";
import SalesRevenueExpenseChart from "./Data/SalesRevenueExpenseChart";
import SalesByMenuItemChart from "./Data/SalesByMenuItemChart";
import SalesByTimeChart from "./Data/SalesByTimeChart";
import SalesStatistics from "./Data/SalesStatistics";
import SalesOperatingHours from "./Data/SalesOperatingHours";
import SalesAverageTime from "./Data/SalesAverageTime";
import SalesPopularMenuItem from "./Data/SalesPopularMenuItem";
import SalesLeastPopularMenuItem from "./Data/SalesLeastPopularMenuItem";
import SalesPopularMenuPerCategory from "./Data/SalesPopularMenuPerCategory";
import SalesCustomerAverage from "./Data/SalesCustomerAverage";
import StoreTitle from "../../component/StoreTitle";

const Sales = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [showAllPopularItems, setShowAllPopularItems] = useState(false);
  const [loading, setLoading] = useState(true);
  const SALES_TYPES = ["ALL", "Dine-In", "Take-Out", "Delivery"];
  const [salesType, setSalesType] = useState("ALL");
  const { user, token, posId } = getAuthCredentials(navigate);
  if (!user || !token || !posId) return null;

  const [dateRange, setDateRange] = useState([
    dayjs().hour(8).minute(0).second(0),
    dayjs(),
  ]);
  const [activeTab, setActiveTab] = useState(0);
  const [pendingRange, setPendingRange] = useState(dateRange);

  const handlePendingRangeChange = (index, newValue) => {
    const newRange = [...pendingRange];
    newRange[index] = newValue;

    if (index === 0 && newRange[0].isAfter(newRange[1])) {
      newRange[1] = newRange[0];
    } else if (index === 1) {
      if (newValue.isAfter(dayjs())) newRange[1] = dayjs();
      if (newRange[0].isAfter(newRange[1])) {
        newRange[0] = newRange[1];
      }
    }
    setPendingRange(newRange);
  };

  const applyDateFilter = () => {
    if (pendingRange[0].isAfter(pendingRange[1])) return;
    setDateRange(pendingRange);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/reports`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              startDate: dateRange[0].format("YYYY-MM-DDTHH:mm:ss"),
              endDate: dateRange[1].format("YYYY-MM-DDTHH:mm:ss"),
              posId,
              category: "Sales",
              salesType,
            },
          }
        );
        setReportData(data);
      } catch (err) {
        console.error("Failed to load sales report:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, token, posId, salesType]);

  const [start, end] = dateRange;
  const rangeLength = end.diff(start, "day") + 1;

  const prevEnd = start.subtract(1, "day");
  const prevStart = prevEnd.subtract(rangeLength - 1, "day");

  const prevRangeLabel = `${prevStart.format("MM/DD")}–${prevEnd.format(
    "MM/DD"
  )}`;

  return (
    <Box sx={{ padding: 3 }}>
      <TitleSetter title="Sales Reports" />
      <Button
        variant="contained"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ marginBottom: 3 }}
      >
        Back
      </Button>

      <Box sx={{ marginTop: -8 }}>
        <StoreTitle title="Sales Reports" />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        centered
        sx={{ marginBottom: 3 }}
      >
        <Tab label="Sales Reports" />
        <Tab label="Transactions" />
        <Tab label="Inventory Reports" />
      </Tabs>

      <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Start Date"
            value={pendingRange[0]}
            onChange={(newValue) => handlePendingRangeChange(0, newValue)}
            maxDateTime={pendingRange[1]}
            sx={{ marginRight: 2 }}
          />

          <DateTimePicker
            label="End Date"
            value={pendingRange[1]}
            onChange={(newValue) => handlePendingRangeChange(1, newValue)}
            minDateTime={pendingRange[0]}
            maxDateTime={dayjs()}
            disableFuture
          />

          <Button
            variant="contained"
            sx={{ ml: 2 }}
            onClick={applyDateFilter}
            disabled={pendingRange[0].isAfter(pendingRange[1])}
          >
            Apply
          </Button>
        </LocalizationProvider>
      </Box>

      {loading ? (
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress size={80} sx={{ mb: 3 }} />
          <Typography variant="h4" align="center">
            Fetching Sales Data
          </Typography>
        </Box>
      ) : activeTab === 0 ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Tabs
              value={salesType}
              onChange={(_, newVal) => setSalesType(newVal)}
              textColor="primary"
              indicatorColor="primary"
            >
              {SALES_TYPES.map((t) => (
                <Tab key={t} value={t} label={t} />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ maxWidth: 900, margin: "0 auto" }}>
            <Typography variant="h5" align="center" gutterBottom>
              Revenue and Expense Over Time
            </Typography>
            <SalesRevenueExpenseChart data={reportData} />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Sales by Menu Item (vs {prevRangeLabel})
            </Typography>
            <Typography
              variant="subtitle2"
              align="center"
              color="text.secondary"
              gutterBottom
            >
              {prevStart.format("MM/DD")}–{prevEnd.format("MM/DD")} vs{" "}
              {start.format("MM/DD")}–{end.format("MM/DD")}
            </Typography>
            <SalesByMenuItemChart data={reportData} />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Sales by Hour (vs {prevRangeLabel})
            </Typography>
            <Typography
              variant="subtitle2"
              align="center"
              color="text.secondary"
              gutterBottom
            >
              {prevStart.format("MM/DD")}–{prevEnd.format("MM/DD")} vs{" "}
              {start.format("MM/DD")}–{end.format("MM/DD")}
            </Typography>
            <SalesByTimeChart data={reportData} />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Average Customer Spend
            </Typography>
            <SalesCustomerAverage data={reportData} />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Average Serving Time
            </Typography>
            <SalesAverageTime data={reportData} />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Operating Hours
            </Typography>
            <SalesOperatingHours data={reportData} />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <SalesPopularMenuItem
              data={reportData}
              start={start}
              end={end}
              prevStart={prevStart}
              prevEnd={prevEnd}
            />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 4 }}>
            <SalesLeastPopularMenuItem
              data={reportData}
              start={start}
              end={end}
              prevStart={prevStart}
              prevEnd={prevEnd}
            />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 4 }}>
            <SalesPopularMenuPerCategory
              data={reportData}
              start={start}
              end={end}
              prevStart={prevStart}
              prevEnd={prevEnd}
            />
          </Box>

          <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 6 }}>
            <SalesStatistics data={reportData} />
          </Box>
        </>
      ) : activeTab === 1 ? (
        <>
          <SalesExport
            user={user}
            dateRange={dateRange}
            activeTab={1}
            data={reportData}
          />
          <SalesTransactions
            dateRange={dateRange}
            token={token}
            posId={posId}
          />
        </>
      ) : (
        <SalesInventory dateRange={dateRange} token={token} posId={posId} />
      )}
    </Box>
  );
};

export default Sales;
